import { useEffect, useState, useSyncExternalStore } from 'react';
import { GamepadState } from './GamepadState';
import { ButtonGroup } from './ButtonGroup';
import { gamepadStore, Position, GamepadLayout } from './store';
import { XInputValues } from '@playcastdotio/CoreTypes';
import { ButtonMeta } from './ButtonMeta';
import { Thumbstick } from './Thumbstick';
import { ButtonBumper } from './ButtonBumper';
import { ButtonTrigger } from './ButtonTrigger';
import { CircularButton } from './CircularButton';
import { ButtonStick } from './ButtonStick';
import clsx from 'clsx';
import { ButtonMenu } from './ButtonMenu';
import { CSSProperties } from 'react';
import { useDraggable } from './useDraggable';
import { AdvanceSettingsSidebar } from './AdvanceSettingsSidebar';
import { GamepadErrorBoundary } from './GamepadErrorBoundary';
import { sendAnalytics, PlaycastAnalyticsOverlayDisplayed } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../metrics';

export type GamepadLayoutKey = keyof GamepadLayout;

interface MobileGamepadUiProps {
  testMode?: boolean;
  isPhysicalGamepadConnected?: boolean;
  disconnect?: () => void;
  statsToggle?: () => void;
  isMouseEnabled?: boolean;
  isKeyboardEnabled?: boolean;
  gamepadSlot?: number;
  customButtonHandler?: () => void;
  customButtonText?: string;
  customButtonIcon?: React.ReactNode;
  // Timer display in Options button (optional)
  timerRemainingSeconds?: number;
  timerTotalSeconds?: number;
  timerPosition?: 'top' | 'bottom';
  /** Switch timer bar to AFK styling (red colors, "AFK" label, pulsing glow) */
  afkMode?: boolean;
  // Theme-based gamepad configuration
  defaultHiddenButtons?: GamepadLayoutKey[];
  invisibleButtons?: GamepadLayoutKey[];
  defaultLayout?: Partial<GamepadLayout>;
  defaultOpacity?: number;
  useCircularButtons?: boolean;
  customButtonIcons?: Partial<Record<GamepadLayoutKey, string>>;
  customButtonSizes?: Partial<Record<GamepadLayoutKey, number>>;
  rightThumbstickBehindButtons?: boolean;
  // Force rotation state - indicates if parent view has been rotated
  isForceRotated?: boolean;
  /** Which button is currently highlighted with the hint hand icon (face, bumper, trigger, d-pad, or meta) */
  attractHighlightedButton?: string;
  /** Glow intensity for the attract highlight (0-100) */
  attractOpacity?: number;
  /** When true, the Options button is visible but not interactive */
  optionsDisabled?: boolean;
  hideOptions?: boolean;
  /** When true, all gamepad control buttons (d-pad, face buttons, sticks, etc.) are hidden
   *  but the bottom menu/exit strip (ButtonMenu) remains visible. Use this when the user
   *  has no gamepad access but still needs the exit option. */
  hideGamepadControls?: boolean;
}

type ScreenState = 'standalone' | 'fullscreen' | 'browser';

const DraggableWrapper = ({
  children,
  layoutKey,
  position,
  scale,
  invisibleButtons,
  lowZIndex,
  rightThumbstickBehindButtons,
}: {
  children: React.ReactNode;
  layoutKey: keyof ReturnType<typeof gamepadStore.getLayout>;
  position: Position;
  scale?: number;
  invisibleButtons?: GamepadLayoutKey[];
  lowZIndex?: boolean;
  rightThumbstickBehindButtons?: boolean;
}) => {
  const { ref, isDragging, isSnapped } = useDraggable(layoutKey);
  const isCustomizing = gamepadStore.getIsGamepadCustomizationOpen();
  const savedOpacity = useSyncExternalStore(gamepadStore.subscribeButtonOpacity, gamepadStore.getButtonOpacity);
  const hiddenButtons = useSyncExternalStore(gamepadStore.subscribeHiddenButtons, gamepadStore.getHiddenButtons);

  // Check if button should be invisible
  // Handle legacy group keys ('dpad', 'faceButtons') by checking if any individual buttons are invisible
  let isInvisible = invisibleButtons?.includes(layoutKey) ?? false;
  if (!isInvisible && layoutKey === 'dpad') {
    // Legacy dpad group - check if any individual dpad buttons are invisible
    isInvisible = invisibleButtons?.some(btn => 
      ['dpadUp', 'dpadDown', 'dpadLeft', 'dpadRight'].includes(btn)
    ) ?? false;
  } else if (!isInvisible && layoutKey === 'faceButtons') {
    // Legacy faceButtons group - check if any individual face buttons are invisible
    isInvisible = invisibleButtons?.some(btn => 
      ['faceButtonA', 'faceButtonB', 'faceButtonX', 'faceButtonY'].includes(btn)
    ) ?? false;
  }

  // Don't render at all if hidden (not just CSS hidden)
  // This prevents touch input from detecting hidden buttons
  // BUT: invisible buttons should still render (just with opacity: 0)
  if (hiddenButtons[layoutKey] && !isCustomizing && !isInvisible) {
    return null;
  }

  // Calculate z-index based on rightThumbstickBehindButtons setting (defaults to true)
  // Apply z-index layering unless explicitly disabled (false)
  const shouldApplyZIndex = rightThumbstickBehindButtons !== false;
  let zIndex: number | undefined = undefined;
  if (shouldApplyZIndex) {
    // When feature is enabled (default), right thumbstick goes to back (z-index: 1)
    // All other buttons go to front (z-index: 10)
    zIndex = lowZIndex ? 1 : 10;
  }

  const style: CSSProperties = {
    position: 'absolute',
    left: position.x,
    top: position.y,
    transform: `translate(-50%, -50%) scale(${scale ?? position.scale})`,
    transition: isDragging ? 'none' : isSnapped ? 'all 0.3s ease-out' : 'all 0.2s ease-out',
    cursor: isCustomizing ? 'move' : 'default',
    touchAction: isCustomizing ? 'none' : 'manipulation',
    // Invisible buttons: opacity 0 but still functional (visibility: visible)
    // Hidden buttons: opacity 0 and not functional (visibility: hidden, but handled by return null above)
    opacity: isInvisible && !isCustomizing ? 0 : hiddenButtons[layoutKey] ? '0' : `${savedOpacity}%`,
    visibility: hiddenButtons[layoutKey] && !isCustomizing && !isInvisible ? 'hidden' : 'visible',
    pointerEvents: isInvisible ? 'auto' : hiddenButtons[layoutKey] ? 'none' : 'auto',
    zIndex,
  };

  return (
    <div ref={ref} style={style} className={clsx('gamepad-button', isCustomizing && 'hover:opacity-80', isDragging && 'opacity-50')} data-gamepad-input>
      {children}
    </div>
  );
};

export const MobileGamepadUi = (props: MobileGamepadUiProps) => {
  const { testMode, invisibleButtons } = props;
  const gamepad = useSyncExternalStore(gamepadStore.subscribeGamepad, gamepadStore.getGamepad);
  const layout = useSyncExternalStore(gamepadStore.subscribeLayout, gamepadStore.getLayout);
  const isAdvanceSettingsOpen = useSyncExternalStore(gamepadStore.subscribeAdvanceSettings, gamepadStore.getIsAdvanceSettingsGamepad);
  // const buttonOpacity = useSyncExternalStore(gamepadStore.subscribeButtonOpacity, gamepadStore.getButtonOpacity);
  const isCustomizing = useSyncExternalStore(gamepadStore.subscribeGamepadCustomization, gamepadStore.getIsGamepadCustomizationOpen);

  // Get theme config (these are stable references that don't change unless setThemeDefaults is called)
  const [useCircularButtons, setUseCircularButtons] = useState(gamepadStore.getUseCircularButtons());
  const [customButtonIcons, setCustomButtonIcons] = useState(gamepadStore.getCustomButtonIcons());
  const [customButtonSizes, setCustomButtonSizes] = useState(gamepadStore.getCustomButtonSizes());

  // Subscribe to theme config changes
  useEffect(() => {
    const unsubscribe = gamepadStore.subscribeThemeConfig((config) => {
      setUseCircularButtons(config.useCircularButtons);
      setCustomButtonIcons(config.customButtonIcons);
      setCustomButtonSizes(config.customButtonSizes);
    });
    return unsubscribe;
  }, []);
  // @ts-expect-error: Property 'standalone' does not exist on type 'Navigator'
  const [status, setStatus] = useState<ScreenState>(window.navigator.standalone ? 'standalone' : 'browser');
  const [scale, setScale] = useState(gamepadStore.getResponsiveScale());

  // Recompute responsive scale on viewport changes. We intentionally do NOT
  // re-derive an `isPortrait` here: the rotation decision lives entirely with
  // `props.isForceRotated` (passed from the parent) and is applied via the
  // CSS @media (orientation: portrait) rule injected below. Having an
  // independent local orientation read previously caused the parent and
  // child to disagree for a frame and stack two `rotate(90deg)` transforms
  // into a 180° flip on iOS Safari.
  useEffect(() => {
    const handleResize = () => {
      setScale(gamepadStore.getResponsiveScale(props.isForceRotated));
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [props.isForceRotated]);

  // Inject the CSS rule that drives the force-landscape rotation. Scoped to a
  // `data-mobile-gamepad-force-landscape` attribute so it never collides with
  // consumer styles. Only one stylesheet is ever inserted, regardless of how
  // many gamepad instances mount.
  useEffect(() => {
    const STYLE_ID = 'mobile-gamepad-force-landscape-styles';
    if (typeof document === 'undefined') return;
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      @media (orientation: portrait) {
        [data-mobile-gamepad-force-landscape='true'] {
          position: fixed;
          top: 50%;
          left: 50%;
          width: 100dvh;
          height: 100dvw;
          transform: translate(-50%, -50%) rotate(var(--force-rotate-angle, 90deg));
          transform-origin: center center;
          transition: transform 200ms ease-out;
        }
      }
      @media (orientation: landscape) {
        [data-mobile-gamepad-force-landscape='true'] {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          transform: none;
        }
      }
    `;
    document.head.appendChild(style);
  }, []);

  // Send analytics when the overlay is displayed
  useEffect(() => {
    if (props.customButtonHandler && window.location.pathname.includes('/j/brainrot')) {
      sendAnalytics({
        name: 'Overlay Displayed',
        role: 'host',
        persistentId: getPersistentPlayerId(),
      } as PlaycastAnalyticsOverlayDisplayed);
    }
  }, [props.customButtonHandler]);

  // const goFullScreen = async () => {
  //   await document.body.requestFullscreen({ navigationUI: 'hide' });
  //   if (document.fullscreenElement) {
  //     setStatus('fullscreen');
  //   }
  // };

  useEffect(() => {
    if (status === 'standalone') return;

    const handleFullscreenChange = () => {
      if (document.fullscreenElement) {
        setStatus('fullscreen');
      } else {
        setStatus('browser');
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [status]);

  return (
    <GamepadErrorBoundary>
      <div
        // The actual rotate(90deg) and fixed-positioning swap to landscape
        // dimensions live in the @media (orientation: portrait) rule injected
        // above (scoped via this data attribute). This keeps the rotation
        // decision atomic with the viewport state — there is no React render
        // between viewport change and transform change, so the prior 90+90=180
        // race is structurally impossible.
        data-mobile-gamepad-force-landscape={props.isForceRotated ? 'true' : undefined}
        className={props.isForceRotated ? 'z-[1000]' : 'absolute inset-0 z-[1000] h-dvh w-full'}
        style={
          {
            ...(props.isForceRotated ? {} : { inset: 0 }),
            touchAction: isAdvanceSettingsOpen ? 'none' : 'auto',
            // Only capture pointer events when customizing (to allow closing by clicking empty space)
            // Otherwise, let clicks pass through to elements underneath (like Play Now button)
            pointerEvents: isCustomizing ? 'auto' : 'none',
          } as React.CSSProperties
        }
        onClick={(e) => {
          if (gamepadStore.getIsGamepadCustomizationOpen() && !(e.target as HTMLElement).closest('.gamepad-button')) {
            e.stopPropagation();
            gamepadStore.setIsGamepadCustomizationOpen(false);
          }
        }}
        onTouchStart={(e) => {
          if (gamepadStore.getIsGamepadCustomizationOpen() && !(e.target as HTMLElement).closest('.gamepad-button')) {
            e.stopPropagation();
            gamepadStore.setIsGamepadCustomizationOpen(false);
          }
        }}
      >
        {/* Top Center Custom Button */}
        {props.customButtonHandler && window.location.pathname.includes('/j/brainrot') && (
          <div className='absolute left-1/2 top-0 z-[1100] flex -translate-x-1/2 items-center justify-center'>
            <button
              onClick={props.customButtonHandler}
              onTouchStart={props.customButtonHandler}
              className='m-0 cursor-pointer border-none bg-transparent p-0 transition-transform hover:scale-110 active:scale-95'
              aria-label={props.customButtonText || 'Special Action'}
              style={{ lineHeight: 0 }}
            >
              <img
                src='/BRAINROTBUTTON.svg'
                alt={props.customButtonText || 'Special Action'}
                className='h-18 m-0 block w-36 p-0'
                draggable={false}
                style={{ display: 'block', margin: 0, padding: 0 }}
              />
            </button>
          </div>
        )}
        <>
          {isCustomizing && (
            <span
              className={clsx(
                'pointer-events-none fixed left-1/2 z-[1001] -translate-x-1/2 rounded-lg bg-blue-400 px-4 py-2 text-sm text-white opacity-80 shadow-lg',
                props.timerPosition === 'top' ? 'bottom-4' : 'top-4'
              )}
              role='img'
              aria-label='Edit mode'
            >
              💡 In edit mode, you're free to move components wherever you like.
            </span>
          )}
          <div
            className={clsx(
              'absolute z-[1000] flex items-end',
              gamepadStore.getIsGamepadCustomizationOpen() ? 'outline outline-4 outline-offset-[-4px] outline-amber-400' : '',
            )}
            style={{
              // Inner row no longer rotates. When `isForceRotated` is true the
              // outer wrapper (above) is already rotated 90° via the CSS @media
              // rule; the inner content lives in the rotated coordinate space
              // and just fills the (now-landscape) container. When
              // `isForceRotated` is false, the outer wrapper isn't rotated and
              // the inner content fills the natural-orientation container the
              // same way. Either way this is a single `rotate(90deg)` at most,
              // never two.
              position: 'absolute',
              width: '100%',
              height: '100%',
              pointerEvents: 'auto',
            } as React.CSSProperties}
          >
            <div className='absolute bottom-0 h-full w-full'>
                {(!props.isPhysicalGamepadConnected || testMode) && !props.hideGamepadControls && (
                  <>
                    {testMode && (
                      <div className='h-max-full w-max-full absolute h-full w-full select-none'>
                        <div className='flex h-full w-full justify-center p-6'>
                          <GamepadState gamepad={gamepad} />
                        </div>
                      </div>
                    )}

                    {/* bumper */}
                    <DraggableWrapper layoutKey='leftBumper' position={layout.leftBumper} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                      {useCircularButtons ? (
                        <CircularButton
                          prefix='left-'
                          buttonType='Bumper'
                          gamepad={gamepad}
                          isPressed={!!(gamepad.wButtons & XInputValues.LEFT_SHOULDER)}
                          iconUrl={customButtonIcons['leftBumper']}
                          size={customButtonSizes['leftBumper']}
                          label='LB'
                          attractHighlight={props.attractHighlightedButton === 'LeftBumper'}
                          attractOpacity={props.attractOpacity}
                        />
                      ) : (
                      <ButtonBumper prefix='left-' gamepad={gamepad} value={XInputValues.LEFT_SHOULDER} />
                      )}
                    </DraggableWrapper>
                    <DraggableWrapper layoutKey='rightBumper' position={layout.rightBumper} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                      {useCircularButtons ? (
                        <CircularButton
                          prefix='right-'
                          buttonType='Bumper'
                          gamepad={gamepad}
                          isPressed={!!(gamepad.wButtons & XInputValues.RIGHT_SHOULDER)}
                          iconUrl={customButtonIcons['rightBumper']}
                          size={customButtonSizes['rightBumper']}
                          label='RB'
                          attractHighlight={props.attractHighlightedButton === 'RightBumper'}
                          attractOpacity={props.attractOpacity}
                        />
                      ) : (
                      <ButtonBumper prefix='right-' gamepad={gamepad} value={XInputValues.RIGHT_SHOULDER} />
                      )}
                    </DraggableWrapper>

                    {/* trigger */}
                    <DraggableWrapper layoutKey='leftTrigger' position={layout.leftTrigger} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                      {useCircularButtons ? (
                        <CircularButton
                          prefix='left-'
                          buttonType='Trigger'
                          gamepad={gamepad}
                          isPressed={!!gamepad.bLeftTrigger}
                          iconUrl={customButtonIcons['leftTrigger']}
                          size={customButtonSizes['leftTrigger']}
                          label='LT'
                          attractHighlight={props.attractHighlightedButton === 'LeftTrigger'}
                          attractOpacity={props.attractOpacity}
                        />
                      ) : (
                      <ButtonTrigger prefix='left-' gamepad={gamepad} value={XInputValues.LEFT_TRIGGER} />
                      )}
                    </DraggableWrapper>
                    <DraggableWrapper layoutKey='rightTrigger' position={layout.rightTrigger} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                      {useCircularButtons ? (
                        <CircularButton
                          prefix='right-'
                          buttonType='Trigger'
                          gamepad={gamepad}
                          isPressed={!!gamepad.bRightTrigger}
                          iconUrl={customButtonIcons['rightTrigger']}
                          size={customButtonSizes['rightTrigger']}
                          label='RT'
                          attractHighlight={props.attractHighlightedButton === 'RightTrigger'}
                          attractOpacity={props.attractOpacity}
                        />
                      ) : (
                      <ButtonTrigger prefix='right-' gamepad={gamepad} value={XInputValues.RIGHT_TRIGGER} />
                      )}
                    </DraggableWrapper>

                    {/* dpad */}
                    {useCircularButtons ? (
                      <>
                        {/* Individual d-pad buttons - separately draggable */}
                        <DraggableWrapper layoutKey='dpadUp' position={layout.dpadUp} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                          <CircularButton
                            prefix='dpad-'
                            buttonType='North'
                            gamepad={gamepad}
                            isPressed={!!(gamepad.wButtons & XInputValues.DPAD_UP)}
                            iconUrl={customButtonIcons['dpadUp']}
                            label='↑'
                            size={customButtonSizes['dpadUp']}
                            attractHighlight={props.attractHighlightedButton === 'DpadUp'}
                            attractOpacity={props.attractOpacity}
                          />
                        </DraggableWrapper>
                        <DraggableWrapper layoutKey='dpadDown' position={layout.dpadDown} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                          <CircularButton
                            prefix='dpad-'
                            buttonType='South'
                            gamepad={gamepad}
                            isPressed={!!(gamepad.wButtons & XInputValues.DPAD_DOWN)}
                            iconUrl={customButtonIcons['dpadDown']}
                            label='↓'
                            size={customButtonSizes['dpadDown']}
                            attractHighlight={props.attractHighlightedButton === 'DpadDown'}
                            attractOpacity={props.attractOpacity}
                          />
                        </DraggableWrapper>
                        <DraggableWrapper layoutKey='dpadLeft' position={layout.dpadLeft} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                          <CircularButton
                            prefix='dpad-'
                            buttonType='West'
                            gamepad={gamepad}
                            isPressed={!!(gamepad.wButtons & XInputValues.DPAD_LEFT)}
                            iconUrl={customButtonIcons['dpadLeft']}
                            label='←'
                            size={customButtonSizes['dpadLeft']}
                            attractHighlight={props.attractHighlightedButton === 'DpadLeft'}
                            attractOpacity={props.attractOpacity}
                          />
                        </DraggableWrapper>
                        <DraggableWrapper layoutKey='dpadRight' position={layout.dpadRight} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                          <CircularButton
                            prefix='dpad-'
                            buttonType='East'
                            gamepad={gamepad}
                            isPressed={!!(gamepad.wButtons & XInputValues.DPAD_RIGHT)}
                            iconUrl={customButtonIcons['dpadRight']}
                            label='→'
                            size={customButtonSizes['dpadRight']}
                            attractHighlight={props.attractHighlightedButton === 'DpadRight'}
                            attractOpacity={props.attractOpacity}
                          />
                        </DraggableWrapper>
                      </>
                    ) : (
                    <DraggableWrapper layoutKey='dpad' position={layout.dpad} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                      <div className='scale-75'>
                        <ButtonGroup
                          prefix='dpad-'
                          gamepad={gamepad}
                          values={{
                            east: XInputValues.DPAD_RIGHT,
                            north: XInputValues.DPAD_UP,
                            south: XInputValues.DPAD_DOWN,
                            west: XInputValues.DPAD_LEFT,
                          }}
                        />
                      </div>
                    </DraggableWrapper>
                    )}

                    {/* face buttons */}
                    {useCircularButtons ? (
                      <>
                        {/* Individual face buttons - separately draggable */}
                        <DraggableWrapper layoutKey='faceButtonY' position={layout.faceButtonY} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                          <CircularButton
                            prefix='right-'
                            buttonType='North'
                            gamepad={gamepad}
                            isPressed={!!(gamepad.wButtons & XInputValues.Y)}
                            iconUrl={customButtonIcons['faceButtonY']}
                            label='Y'
                            size={customButtonSizes['faceButtonY']}
                            attractHighlight={props.attractHighlightedButton === 'North' || props.attractHighlightedButton === 'ALL'}
                            attractOpacity={props.attractOpacity}
                          />
                        </DraggableWrapper>
                        <DraggableWrapper layoutKey='faceButtonB' position={layout.faceButtonB} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                          <CircularButton
                            prefix='right-'
                            buttonType='East'
                            gamepad={gamepad}
                            isPressed={!!(gamepad.wButtons & XInputValues.B)}
                            iconUrl={customButtonIcons['faceButtonB']}
                            label='B'
                            size={customButtonSizes['faceButtonB']}
                            attractHighlight={props.attractHighlightedButton === 'East' || props.attractHighlightedButton === 'ALL'}
                            attractOpacity={props.attractOpacity}
                          />
                        </DraggableWrapper>
                        <DraggableWrapper layoutKey='faceButtonA' position={layout.faceButtonA} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                          <CircularButton
                            prefix='right-'
                            buttonType='South'
                            gamepad={gamepad}
                            isPressed={!!(gamepad.wButtons & XInputValues.A)}
                            iconUrl={customButtonIcons['faceButtonA']}
                            label='A'
                            size={customButtonSizes['faceButtonA']}
                            attractHighlight={props.attractHighlightedButton === 'South' || props.attractHighlightedButton === 'ALL'}
                            attractOpacity={props.attractOpacity}
                          />
                        </DraggableWrapper>
                        <DraggableWrapper layoutKey='faceButtonX' position={layout.faceButtonX} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                          <CircularButton
                            prefix='right-'
                            buttonType='West'
                            gamepad={gamepad}
                            isPressed={!!(gamepad.wButtons & XInputValues.X)}
                            iconUrl={customButtonIcons['faceButtonX']}
                            label='X'
                            size={customButtonSizes['faceButtonX']}
                            attractHighlight={props.attractHighlightedButton === 'West' || props.attractHighlightedButton === 'ALL'}
                            attractOpacity={props.attractOpacity}
                          />
                        </DraggableWrapper>
                      </>
                    ) : (
                    <DraggableWrapper layoutKey='faceButtons' position={layout.faceButtons} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                      <ButtonGroup
                        prefix='right-'
                        gamepad={gamepad}
                        values={{
                          east: XInputValues.B,
                          north: XInputValues.Y,
                          south: XInputValues.A,
                          west: XInputValues.X,
                        }}
                        attractHighlightedButton={props.attractHighlightedButton}
                        attractOpacity={props.attractOpacity}
                      />
                    </DraggableWrapper>
                    )}

                    {/* thumbsticks */}
                    <DraggableWrapper layoutKey='leftThumbstick' position={layout.leftThumbstick} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                      <div className={clsx('overflow-visible', isCustomizing && 'pointer-events-none')}>
                        <Thumbstick
                          location='left'
                          gamepad={gamepad}
                          value={XInputValues.LEFT_THUMB}
                          customIcon={customButtonIcons['leftThumbstick']}
                          customSize={customButtonSizes['leftThumbstick']}
                          useCircularStyle={useCircularButtons}
                        />
                      </div>
                    </DraggableWrapper>
                    <DraggableWrapper
                      layoutKey='rightThumbstick'
                      position={layout.rightThumbstick}
                      scale={scale}
                      invisibleButtons={invisibleButtons}
                      lowZIndex={props.rightThumbstickBehindButtons !== false}
                      rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}
                    >
                      <div className={clsx('overflow-visible', isCustomizing && 'pointer-events-none')}>
                        <Thumbstick
                          location='right'
                          gamepad={gamepad}
                          value={XInputValues.RIGHT_THUMB}
                          customIcon={customButtonIcons['rightThumbstick']}
                          customSize={customButtonSizes['rightThumbstick']}
                          useCircularStyle={useCircularButtons}
                        />
                      </div>
                    </DraggableWrapper>

                    {/* meta buttons */}
                    <DraggableWrapper layoutKey='leftMeta' position={layout.leftMeta} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                      {useCircularButtons ? (
                        <CircularButton
                          prefix='left-'
                          gamepad={gamepad}
                          isPressed={!!(gamepad.wButtons & XInputValues.BACK)}
                          iconUrl={customButtonIcons['leftMeta']}
                          size={customButtonSizes['leftMeta'] || 60}
                          label='Select'
                          buttonType='Meta'
                          attractHighlight={props.attractHighlightedButton === 'Select'}
                          attractOpacity={props.attractOpacity}
                        />
                      ) : (
                        <ButtonMeta prefix='left-' gamepad={gamepad} value={XInputValues.BACK} />
                      )}
                    </DraggableWrapper>
                    <DraggableWrapper layoutKey='rightMeta' position={layout.rightMeta} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                      {useCircularButtons ? (
                        <CircularButton
                          prefix='right-'
                          gamepad={gamepad}
                          isPressed={!!(gamepad.wButtons & XInputValues.START)}
                          iconUrl={customButtonIcons['rightMeta']}
                          size={customButtonSizes['rightMeta'] || 60}
                          label='Start'
                          buttonType='Meta'
                          attractHighlight={props.attractHighlightedButton === 'Start'}
                          attractOpacity={props.attractOpacity}
                        />
                      ) : (
                        <ButtonMeta prefix='right-' gamepad={gamepad} value={XInputValues.START} />
                      )}
                    </DraggableWrapper>

                    {/* button stick */}
                    <DraggableWrapper layoutKey='leftStick' position={layout.leftStick} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                      {useCircularButtons ? (
                        <CircularButton
                          prefix='left-'
                          gamepad={gamepad}
                          isPressed={!!(gamepad.wButtons & XInputValues.LEFT_THUMB)}
                          iconUrl={customButtonIcons['leftStick']}
                          size={customButtonSizes['leftStick'] || 60}
                          label='L3'
                          buttonType='StickClick'
                        />
                      ) : (
                      <ButtonStick gamepad={gamepad} value={XInputValues.LEFT_THUMB} prefix='left-' />
                      )}
                    </DraggableWrapper>
                    <DraggableWrapper layoutKey='rightStick' position={layout.rightStick} scale={scale} invisibleButtons={invisibleButtons} rightThumbstickBehindButtons={props.rightThumbstickBehindButtons}>
                      {useCircularButtons ? (
                        <CircularButton
                          prefix='right-'
                          gamepad={gamepad}
                          isPressed={!!(gamepad.wButtons & XInputValues.RIGHT_THUMB)}
                          iconUrl={customButtonIcons['rightStick']}
                          size={customButtonSizes['rightStick'] || 60}
                          label='R3'
                          buttonType='StickClick'
                        />
                      ) : (
                      <ButtonStick gamepad={gamepad} value={XInputValues.RIGHT_THUMB} prefix='right-' />
                      )}
                    </DraggableWrapper>
                  </>
                )}

                {/* menu */}
                {!props.hideOptions && (
                <div className='gamepad-button absolute bottom-0 w-full'>
                  <ButtonMenu
                    disconnect={props.disconnect}
                    statsToggle={props.statsToggle}
                    isMouseEnabled={props.isMouseEnabled}
                    isKeyboardEnabled={props.isKeyboardEnabled}
                    gamepadSlot={props.gamepadSlot}
                    timerRemainingSeconds={props.timerRemainingSeconds}
                    timerTotalSeconds={props.timerTotalSeconds}
                    timerPosition={props.timerPosition}
                    optionsDisabled={props.optionsDisabled}
                    afkMode={props.afkMode}
                  />
                </div>
                )}
              </div>
            </div>
        </>
        <AdvanceSettingsSidebar />
      </div>
    </GamepadErrorBoundary>
  );
};

